/**
 * HeroImagePipeline · Cruise Command Hero Card System (P0 · PO 2026-02-11)
 *
 *   One image-handling system → many Hero consumers.
 *
 * The pipeline owns every image concern common to both Hero families:
 *   · canonical URL resolution (relative → absolute via REACT_APP_BACKEND_URL)
 *   · focal-point normalisation (clamped `{x, y}` in [0..1])
 *   · loading state (fade-in on decode)
 *   · fallback ladder driven by `image_source_type`
 *   · terminal tinted brand shim (CSS gradient — never a broken <img>)
 *   · provenance / tint exposure for family-specific compositing
 *
 * Compositing (Ken Burns, mood wash, grain, gradients) stays inside
 * `WorkspaceHero` and `EditorialHero`. The pipeline is intentionally
 * unopinionated about final visual treatment.
 *
 * Usage (render-prop / hook):
 *
 *   <HeroImagePipeline data={heroData}>
 *     {({ src, alt, focalPoint, tint, isFallback, fallbackTier,
 *         onLoad, isLoaded }) => (
 *       <YourFamilyComposition ... />
 *     )}
 *   </HeroImagePipeline>
 *
 *   const state = useHeroImage(heroData);
 *
 * Contract (see /app/memory/HERO_SYSTEM.md · "HeroData Data Contract"):
 *   data = {
 *     image, alt, focal_point, tint, source_provenance,
 *     image_source_type, eyebrow, title, subtitle, meta, action
 *   }
 *
 * Only `image`, `alt`, `focal_point`, `tint`, `source_provenance` and
 * `image_source_type` are consumed here; the rest belong to the
 * family renderer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ── Fallback ladders per `image_source_type` ────────────────
// Every ladder terminates in `null` — the caller must render the
// tinted brand shim when it walks off the end. That shim is a CSS
// gradient (see `renderBrandShim` below) so the traveller never
// sees a broken `<img>` icon.
//
// The intermediate entries are optional editorial defaults hosted in
// `/public/brand/hero-defaults/`. If the file is missing the pipeline
// simply advances to the next tier; nothing crashes.
const FALLBACK_LADDERS = {
  ship: [
    "/brand/hero-defaults/ship-ocean.jpg",
    "/brand/hero-defaults/editorial-ocean.jpg",
    null,
  ],
  port: [
    "/brand/hero-defaults/port-coastal.jpg",
    "/brand/hero-defaults/editorial-coastal.jpg",
    null,
  ],
  cruise_line: [
    "/brand/hero-defaults/cruise-line.jpg",
    "/brand/hero-defaults/editorial-ocean.jpg",
    null,
  ],
  destination: [
    "/brand/hero-defaults/destination.jpg",
    "/brand/hero-defaults/editorial-coastal.jpg",
    null,
  ],
  editorial: [
    "/brand/hero-defaults/editorial.jpg",
    null,
  ],
  generic: [
    "/brand/hero-defaults/editorial.jpg",
    null,
  ],
};


// ── Helpers ─────────────────────────────────────────────────
const clamp01 = (v, fallback) =>
  typeof v === "number" && v >= 0 && v <= 1 ? v : fallback;


/** Resolve any relative URL against REACT_APP_BACKEND_URL. */
export function resolveHeroImageUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) {
    // Absolute path — relative to backend base when set, otherwise
    // relative to the frontend origin (safe for /public assets).
    if (raw.startsWith("/brand/") || raw.startsWith("/static/")) return raw;
    return `${BACKEND_BASE}${raw}`;
  }
  return raw;
}


/** Normalise the caller's `HeroData` into pipeline inputs. */
export function normaliseHeroData(data) {
  const d = data || {};
  const initialSrc = resolveHeroImageUrl(d.image);
  const ladder = FALLBACK_LADDERS[d.image_source_type] || FALLBACK_LADDERS.generic;
  return {
    initialSrc,
    ladder,
    alt: d.alt || d.title || "",
    focalPoint: {
      x: clamp01(d.focal_point?.x, 0.5),
      y: clamp01(d.focal_point?.y, 0.5),
    },
    tint: d.tint || null,
    provenance: d.source_provenance || null,
    sourceType: d.image_source_type || "generic",
  };
}


/**
 * `useHeroImage` — the hook flavour.
 *
 * Walks the fallback ladder on `<img>` error events and exposes:
 *   src            — currently rendered URL (may be null → shim)
 *   isLoaded       — image has decoded
 *   isFallback     — we are not on the caller's original `image`
 *   fallbackTier   — 0 if original, 1..N as we advance the ladder
 *   onImageError   — bind to `<img onError={...}>`
 *   onImageLoad    — bind to `<img onLoad={...}>`
 *   focalPoint     — normalised `{x, y}`
 *   tint           — passthrough for the family renderer
 *   provenance     — machine key (never rendered directly)
 */
export function useHeroImage(data) {
  const normalised = useMemo(() => normaliseHeroData(data), [data]);
  const [tier, setTier] = useState(normalised.initialSrc ? 0 : 1);
  const [isLoaded, setLoaded] = useState(false);

  // Reset when the caller's `image` changes.
  useEffect(() => {
    setTier(normalised.initialSrc ? 0 : 1);
    setLoaded(false);
  }, [normalised.initialSrc]);

  const currentSrc = tier === 0
    ? normalised.initialSrc
    : (normalised.ladder[tier - 1] || null);

  const onImageError = useCallback(() => {
    setLoaded(false);
    setTier((t) => t + 1);
  }, []);
  const onImageLoad = useCallback(() => setLoaded(true), []);

  return {
    src: currentSrc,
    isLoaded,
    isFallback: tier > 0,
    fallbackTier: tier,
    onImageError,
    onImageLoad,
    focalPoint: normalised.focalPoint,
    tint: normalised.tint,
    provenance: normalised.provenance,
    alt: normalised.alt,
    sourceType: normalised.sourceType,
  };
}


/**
 * `renderBrandShim` — the terminal CSS gradient shown when every
 * URL in the ladder is exhausted. Never emoji, never broken image.
 * Callers may pass a `tint` to bias the gradient toward the
 * itinerary's environmental mood.
 */
export function renderBrandShim({ tint, testId } = {}) {
  const primary = tint?.primary || "#0f2237";
  const secondary = tint?.secondary || "#d4af37";
  return (
    <div
      aria-hidden
      data-testid={testId || "hero-brand-shim"}
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          `radial-gradient(1200px 400px at -10% -10%, ${primary}55 0%, transparent 60%),` +
          `radial-gradient(900px 320px at 110% 20%, ${secondary}33 0%, transparent 55%),` +
          "linear-gradient(135deg, #0a1628 0%, #0f2237 55%, #14304a 100%)",
      }}
    />
  );
}


/**
 * `HeroImagePipeline` — the render-prop flavour. Both families use
 * this so `<img>` is rendered exactly once in the codebase.
 *
 * The `children` prop receives the full hook return value plus a
 * bound `imgProps` object so families can spread it directly onto
 * their own `<img>` without duplicating the boilerplate.
 */
export function HeroImagePipeline({
  data,
  children,
  imgClassName = "absolute inset-0 w-full h-full object-cover",
  imgStyleExtra = {},
  testId,
}) {
  const state = useHeroImage(data);
  const x = Math.round(state.focalPoint.x * 100);
  const y = Math.round(state.focalPoint.y * 100);

  const imgProps = state.src
    ? {
        src: state.src,
        alt: state.alt || "",
        loading: "eager",
        decoding: "async",
        onError: state.onImageError,
        onLoad: state.onImageLoad,
        className: imgClassName,
        style: {
          opacity: state.isLoaded ? 1 : 0,
          transition: "opacity 480ms ease-out",
          objectPosition: `${x}% ${y}%`,
          ...imgStyleExtra,
        },
        "data-testid": testId ? `${testId}-img` : "hero-image",
        "data-hero-fallback-tier": state.fallbackTier,
        "data-loaded": state.isLoaded ? "true" : "false",
      }
    : null;

  const brandShim = !state.src
    ? renderBrandShim({ tint: state.tint, testId: testId ? `${testId}-shim` : undefined })
    : null;

  return children({
    ...state,
    imgProps,
    brandShim,
  });
}


export default HeroImagePipeline;
